/**
 * Kinetic pipeline class detection + per-class engine config.
 *
 * The same kinetic tool catalog runs through three structurally different
 * capture pipelines. Knowing the class up-front lets the browser render
 * the right UI, emit the right event vocabulary, and feed the SBN
 * adjudicator a payload it can score.
 *
 * Mirrors:
 *   - services/pgdag-py/app/steps/ai_adjudication.py::_detect_pipeline_class
 *   - services/kts/src/lib/pgdag-bridge.ts::resolveWorkflowForTool
 */

export type PipelineClass = 'rep_counter' | 'timed_hold' | 'explosive_timer';

/**
 * Resolve which pipeline class a tool belongs to from its id.
 *
 * Recognized id namespaces:
 *   - "kinetic.{movement}.{mode}.v{n}"               canonical
 *   - "prospect.kinetic.{movement}.{mode}.v{n}"      newer prospect-scoped
 *   - "prospect.combine_{movement}.v{n}"             legacy production
 *
 * Defaults to 'rep_counter' when nothing matches — that's the safest
 * fallback because rep_counter has the most validation and the engine
 * exists today.
 */
export function detectPipelineClass(toolId: string | undefined | null): PipelineClass {
  const id = (toolId || '').toLowerCase();

  // Timed hold movements: plank, wall sit, dead hang, etc.
  if (
    id.includes('plank') ||
    id.includes('wall_sit') ||
    id.includes('wallsit') ||
    id.includes('wall-sit') ||
    id.includes('.hold.') ||
    id.includes('dead_hang') ||
    id.includes('dead-hang')
  ) {
    return 'timed_hold';
  }

  // Explosive-timer movements: sprints, jumps, reaction-time tests
  if (
    id.includes('sprint') ||
    id.includes('forty_yd') ||
    id.includes('40yd') ||
    id.includes('broad_jump') ||
    id.includes('vertical_jump') ||
    id.includes('reaction')
  ) {
    return 'explosive_timer';
  }

  return 'rep_counter';
}

/** Per-class display + UX config the KtsMount component can read. */
export interface PipelineClassConfig {
  /** Headline shown above the camera while waiting to start. */
  primary_metric_label: string;
  /** Verb for the start button. */
  start_label: string;
  /** Verb for the stop button. */
  stop_label: string;
  /** Whether the browser must record video for coach review. */
  require_video: boolean;
  /** Whether voice trigger ("go") starts the session. */
  voice_trigger: boolean;
  /** Soft duration cap in seconds (0 = open-ended hold or unset). */
  duration_s: number;
  /** Whether this class is fully implemented in the browser engine yet. */
  fully_implemented: boolean;
}

const REP_COUNTER_CONFIG: PipelineClassConfig = {
  primary_metric_label: 'reps',
  start_label: 'Start',
  stop_label: 'Finish',
  require_video: true,
  voice_trigger: false,
  duration_s: 60,
  fully_implemented: true,
};

const TIMED_HOLD_CONFIG: PipelineClassConfig = {
  primary_metric_label: 'seconds held',
  start_label: 'Start hold',
  stop_label: 'End hold',
  require_video: true,
  voice_trigger: false,
  duration_s: 0, // open-ended; ends when athlete releases or form breaks
  fully_implemented: false, // engine TODO — uses fallback timer for now
};

const EXPLOSIVE_TIMER_CONFIG: PipelineClassConfig = {
  primary_metric_label: 'seconds',
  start_label: 'Ready',
  stop_label: 'Done',
  require_video: true,
  voice_trigger: true,
  duration_s: 30, // generous cap; sprints typically <10s
  fully_implemented: false, // engine TODO — uses fallback timer for now
};

const CONFIG: Record<PipelineClass, PipelineClassConfig> = {
  rep_counter: REP_COUNTER_CONFIG,
  timed_hold: TIMED_HOLD_CONFIG,
  explosive_timer: EXPLOSIVE_TIMER_CONFIG,
};

export function getPipelineClassConfig(klass: PipelineClass): PipelineClassConfig {
  return CONFIG[klass];
}

/**
 * Canonical event-name vocabulary the adjudicator (Claude tool-use) expects
 * per class. Browser engines emit these names so the seal flow translates
 * 1:1 without mapping.
 */
export const PIPELINE_CLASS_EVENTS: Record<PipelineClass, readonly string[]> = {
  rep_counter: [
    'calibration_locked',
    'down_entered',
    'up_returned',
    'rep_complete',
    'form_quality',
    'session_summary',
  ],
  timed_hold: [
    'calibration_locked',
    'hold_started',
    'hold_maintained',
    'form_break',
    'hold_ended',
    'session_summary',
  ],
  explosive_timer: [
    'calibration_locked',
    'voice_go',
    'motion_start',
    'motion_peak',
    'motion_end',
    'session_summary',
  ],
};
